import {
  iAgentDataBlock,
  iFeedbackBlockData,
  iHolidayQuoteContentBlocks,
  iHolidayQuotePricingOptionData,
  iInclusionBlockData,
  iResourcesBlockData,
  iMapSummaryBlockData,
  iDestinationSummaryBlockData,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { useHolidayQuoteBrand } from "./contexts/use-holiday-quote-brand";
import { useState } from "react";
import { AgentSection } from "./content-blocks/agent-section";
import { FeedbackSection } from "./content-blocks/feedback-section";
import { InclusionsSection } from "./content-blocks/inclusions-section";
import { PricingSection } from "./content-blocks/pricing-section";
import { ResourcesSection } from "./content-blocks/resources-section";
import { NotesSection } from "./content-blocks/notes-section";
import { TrustPilotSection } from "./content-blocks/trustpilot-section";
import { CTASection } from "./content-blocks/cta-section";
import { ConfirmBookingSection } from "./content-blocks/confirm-booking-section";
import { RequestAmendmentsSection } from "./content-blocks/request-amendments-section";
import { MapSummarySection } from "./content-blocks/map-summary-section";
import { DestinationSummarySection } from "./content-blocks/destination-summary-section";
import { CustomTemplateBlockSection } from "./content-blocks/custom-template-block-section";
import { FinePrintSection } from "./content-blocks/fine-print-section";
import { NoteWithGallerySection } from "./content-blocks/note-with-gallery-section";
import { DayByDayItinerarySection } from "./content-blocks/day-by-day-itinerary-section";
import { ColumnSection } from "./content-blocks/column-section";
import { GlobalErrorBoundary } from "@/error-boundary";
import { BlockErrorFallback } from "../block-error-fallback";
export const QuoteContentSection = ({
  blocks,
}: {
  blocks: iHolidayQuoteContentBlocks[];
}) => {
  const { primaryColor } = useHolidayQuoteBrand();
  return (
    <>
      <div>
        {blocks.map((block, index) => {
          return (
            <div key={block.id} className="my-4" id={`content-${block.id}`}>
              <ContentBlock block={block} primaryColor={primaryColor} />
            </div>
          );
        })}
      </div>
    </>
  );
};

export const ContentBlock = ({
  block,
  primaryColor,
}: {
  block: iHolidayQuoteContentBlocks;
  primaryColor: string;
}) => {
  return (
    <GlobalErrorBoundary FallbackComponent={BlockErrorFallback} data={block}>
      <_ContentBlock block={block} primaryColor={primaryColor} />
    </GlobalErrorBoundary>
  );
};

const _ContentBlock = ({
  block,
  primaryColor,
}: {
  block: iHolidayQuoteContentBlocks;
  primaryColor: string;
}) => {
  switch (block.type) {
    case "inclusions":
      return (
        <InclusionsSection
          {...block.inclusionsData!}
          inclusions={block.inclusionsData?.inclusions ?? []}
          key={block.id}
          primaryColor={primaryColor}
        />
      );
    case "pricing_option":
      return (
        <PricingSection
          key={block.id}
          {...block.pricingOptionData!}
          primaryColor={primaryColor}
        />
      );
    case "agent_data":
      return (
        <AgentSection
          key={block.id}
          {...block.agentData}
          primaryColor={primaryColor}
        />
      );
    case "quote_feedback":
      return (
        <FeedbackSection
          key={block.id}
          {...block.quoteFeedbackData}
          primaryColor={primaryColor}
        />
      );
    case "resources":
      return <ResourcesSection key={block.id} {...block.resourcesData} />;
    case "notes":
      return (
        <NotesSection key={block.id} heading={block.title} notes={block.body} />
      );
    case "trustpilot_reviews":
      return <TrustPilotSection key={block.id} {...block.trustPilotData} />;
    case "cta_block":
      return (
        <CTASection
          key={block.id}
          {...block.ctaBlockData}
          primaryColor={primaryColor}
        />
      );
    case "confirm_booking_block":
      return (
        <ConfirmBookingSection
          key={block.id}
          {...block.confirmBookingBlockData}
          primaryColor={primaryColor}
        />
      );
    case "request_amendments_block":
      return (
        <RequestAmendmentsSection
          key={block.id}
          {...block.requestAmendmentsBlockData}
          primaryColor={primaryColor}
        />
      );
    case "map_summary":
      return (
        <MapSummarySection
          key={block.id}
          data={block.mapSummaryData!}
          primaryColor={primaryColor}
        />
      );
    case "destination_summary":
      return (
        <DestinationSummarySection
          key={block.id}
          data={block.destinationSummaryData!}
          primaryColor={primaryColor}
        />
      );
    case "custom_template_block":
      return (
        <CustomTemplateBlockSection
          key={block.id}
          {...(block.customTemplateBlockData ?? { html: "" })}
        />
      );
    case "fine_print":
      return (
        <FinePrintSection
          key={block.id}
          heading={block.title}
          gallery={block.gallery}
          finePrintData={block.finePrintData}
        />
      );
    case "note_with_gallery":
      return (
        <NoteWithGallerySection
          key={block.id}
          heading={block.title}
          noteWithGalleryData={block.noteWithGalleryData}
        />
      );
    case "day_by_day_itinerary":
      return (
        <DayByDayItinerarySection
          key={block.id}
          heading={block.title}
          dayByDayItineraryData={block.dayByDayItineraryData}
        />
      );
    case "column":
      return (
        <ColumnSection
          key={block.id}
          columnData={block.columnData}
          renderBlock={(child) => (
            <ContentBlock block={child} primaryColor={primaryColor} />
          )}
        />
      );
    default:
      return <></>;
  }
};
